import { useState } from "react";
import { Box, Typography, Menu, MenuItem, ListItemIcon, Divider } from "@mui/material";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import LogoutIcon from "@mui/icons-material/Logout";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useThemeMode } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { TOOL_CATEGORIES } from "./Sidebar";
import LiquidGlassCard from "./LiquidGlassCard";

function getUiRoleLabel(roles = []) {
  if (roles.includes("admin")) return "Admin";
  if (roles.includes("analytics_viewer")) return "User";
  return "User";
}
export default function WelcomeScreen({ onSelectTool, activeCategory, onSelectCategory, onOpenChangePassword }) {
  const { mode } = useThemeMode();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);

  const visibleCategories = TOOL_CATEGORIES.map(category => ({
    ...category,
    tools: category.tools.filter(
      (t) => user?.roles?.includes("admin") || t.allowedRoles.some(r => user?.roles?.includes(r))
    )
  })).filter(category => category.tools.length > 0);

  const displayCategories = activeCategory
    ? visibleCategories.filter(c => c.id === activeCategory.id)
    : [];

  const handleCardClick = (tool) => {
    if (tool.upcoming) return;
    if (onSelectTool) onSelectTool(tool);
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        bgcolor: mode === "light" ? "#f0f2f5" : "#0d1117",
        overflowY: "auto",
        overflowX: "hidden",
        position: "relative",
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-thumb": {
          background: "rgba(232,93,26,0.25)",
          borderRadius: "10px",
        },
      }}
    >
      {/* ── Decorative background ── */}
      <Box sx={{ position: "absolute", width: 560, height: 560, borderRadius: "50%", background: "radial-gradient(circle, rgba(232,93,26,0.10) 0%, transparent 65%)", top: "-8%", right: 0, transform: "translateX(30%)", pointerEvents: "none", zIndex: 0 }} />
      <Box sx={{ position: "absolute", width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle, rgba(168,85,247,0.07) 0%, transparent 65%)", bottom: "8%", left: 0, transform: "translateX(-30%)", pointerEvents: "none", zIndex: 0 }} />
      <Box sx={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 65%)", top: "42%", left: "22%", pointerEvents: "none", zIndex: 0 }} />
      <Box sx={{ position: "absolute", inset: 0, backgroundImage: `radial-gradient(${mode === "light" ? "rgba(0,0,0,0.055)" : "rgba(255,255,255,0.03)"} 1px, transparent 1px)`, backgroundSize: "24px 24px", pointerEvents: "none", zIndex: 0 }} />

      {/* ── Top Bar ── */}
      <Box
        sx={{
          width: "100%",
          bgcolor: mode === "light" ? "rgba(255,255,255,0.82)" : "rgba(22,27,34,0.80)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          borderBottom: `1px solid ${mode === "light" ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.07)"}`,
          px: 3,
          py: 0,
          minHeight: "68px",
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          position: "relative",
          zIndex: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
          <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                position: "absolute",
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: mode === 'light' ? "rgba(232,93,26,0.1)" : "rgba(232,93,26,0.15)",
                filter: "blur(10px)",
                zIndex: 0,
              }}
            />
            <Box
              component="img"
              src={mode === "light" ? "/edge-ai-logo-black.png" : "/edge-ai-logo-white.png"}
              alt="Assist-Edgex Copilot"
              sx={{ height: 19, objectFit: "contain", position: "relative", zIndex: 1 }}
            />
          </Box>
        </Box>

        {/* Breadcrumb — category name only, no "All" tab */}
        {activeCategory && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 0.5 }}>
            <Typography sx={{ fontSize: "13px", color: "#8fa3c0" }}>›</Typography>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#8fa3c0" }}>
              {activeCategory.category}
            </Typography>
          </Box>
        )}

        {/* ── User chip ── */}
        <Box sx={{ ml: "auto", flexShrink: 0 }}>
          <Box
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.6,
              borderRadius: "20px",
              border: `1px solid ${mode === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.1)"}`,
              bgcolor: mode === "light" ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.04)",
              cursor: "pointer",
              transition: "all 0.18s ease",
              "&:hover": {
                border: "1px solid rgba(242,101,34,0.35)",
                bgcolor: "rgba(242,101,34,0.06)",
              },
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: "rgba(242,101,34,0.12)",
                border: "1.5px solid rgba(242,101,34,0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "11px",
                fontWeight: 700,
                color: "#F26522",
                flexShrink: 0,
              }}
            >
              {user?.email?.charAt(0).toUpperCase()}
            </Box>
            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: mode === "light" ? "#0f172a" : "#ffffff", lineHeight: 1.2, whiteSpace: "nowrap" }}>
                {user?.email?.split("@")[0]}
              </Typography>
              <Typography sx={{ fontSize: "9px", fontWeight: 700, color: "#F26522", textTransform: "uppercase", letterSpacing: "0.4px", lineHeight: 1 }}>
                {getUiRoleLabel(user?.roles)}
              </Typography>
            </Box>
          </Box>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  minWidth: 220,
                  borderRadius: "16px",
                  border: `1px solid ${mode === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.1)"}`,
                  boxShadow: "0 16px 40px rgba(0,0,0,0.14)",
                  bgcolor: mode === "light" ? "#ffffff" : "#161b22",
                  overflow: "hidden",
                  p: 0,
                }
              }
            }}
          >
            {/* Profile header inside menu */}
            <Box
              sx={{
                px: 2,
                py: 1.8,
                background: mode === "light"
                  ? "linear-gradient(135deg, rgba(242,101,34,0.08) 0%, rgba(245,158,11,0.05) 100%)"
                  : "linear-gradient(135deg, rgba(242,101,34,0.14) 0%, rgba(245,158,11,0.08) 100%)",
                borderBottom: `1px solid ${mode === "light" ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.07)"}`,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  bgcolor: "rgba(242,101,34,0.15)",
                  border: "2px solid rgba(242,101,34,0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "15px",
                  fontWeight: 700,
                  color: "#F26522",
                  flexShrink: 0,
                }}
              >
                {user?.email?.charAt(0).toUpperCase()}
              </Box>
              <Box>
                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: mode === "light" ? "#0f172a" : "#ffffff", lineHeight: 1.3 }}>
                  {user?.email?.split("@")[0]}
                </Typography>
                <Typography sx={{ fontSize: "10px", color: "#8fa3c0", lineHeight: 1.2, mt: 0.2 }}>
                  {user?.email}
                </Typography>
                <Box
                  sx={{
                    display: "inline-flex",
                    mt: 0.5,
                    px: 0.8,
                    py: 0.15,
                    borderRadius: "6px",
                    bgcolor: "rgba(242,101,34,0.12)",
                    border: "1px solid rgba(242,101,34,0.25)",
                  }}
                >
                  <Typography sx={{ fontSize: "9px", fontWeight: 700, color: "#F26522", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {getUiRoleLabel(user?.roles)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Menu items */}
            <Box sx={{ p: 0.75 }}>
              {user?.roles?.includes("admin") && (
                <MenuItem
                  onClick={() => { setMenuAnchor(null); navigate("/admin"); }}
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    borderRadius: "10px",
                    px: 1.5,
                    py: 1,
                    color: mode === "light" ? "#334155" : "#cbd5e1",
                    "&:hover": { bgcolor: "rgba(242,101,34,0.08)", color: "#F26522",
                      "& .menu-icon": { color: "#F26522" }
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ManageAccountsIcon className="menu-icon" sx={{ fontSize: 18, color: "#8fa3c0", transition: "color 0.2s" }} />
                  </ListItemIcon>
                  Manage Users
                </MenuItem>
              )}

              <MenuItem
                onClick={() => { setMenuAnchor(null); onOpenChangePassword?.(); }}
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  borderRadius: "10px",
                  px: 1.5,
                  py: 1,
                  color: mode === "light" ? "#334155" : "#cbd5e1",
                  "&:hover": { bgcolor: "rgba(242,101,34,0.08)", color: "#F26522",
                    "& .menu-icon": { color: "#F26522" }
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <LockResetIcon className="menu-icon" sx={{ fontSize: 18, color: "#8fa3c0", transition: "color 0.2s" }} />
                </ListItemIcon>
                Reset Password
              </MenuItem>

              <Divider sx={{ my: 0.5, borderColor: mode === "light" ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.07)" }} />

              <MenuItem
                onClick={() => { setMenuAnchor(null); logout(); }}
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  borderRadius: "10px",
                  px: 1.5,
                  py: 1,
                  color: "#e53935",
                  "&:hover": { bgcolor: "rgba(229,57,53,0.08)" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <LogoutIcon sx={{ fontSize: 18, color: "inherit" }} />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Box>
          </Menu>
        </Box>
      </Box>

      {/* ── Main Content ── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* ── Empty State — full page glass ── */}
        {!activeCategory && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              background: mode === "light" ? "rgba(255,255,255,0.60)" : "rgba(10,18,40,0.48)",
              backdropFilter: "blur(28px)",
              WebkitBackdropFilter: "blur(28px)",
              userSelect: "none",
            }}
          >
            {/* rainbow accent bar */}
            <Box sx={{ height: 4, flexShrink: 0, background: "linear-gradient(90deg, #F26522 0%, #a855f7 50%, #3b82f6 100%)" }} />

            {/* centered content */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2.5,
                px: { xs: 3, md: 8 },
                py: 5,
                maxWidth: 860,
                width: "100%",
                mx: "auto",
              }}
            >
              <Typography
                sx={{
                  fontSize: "26px",
                  fontWeight: 800,
                  background: mode === "light"
                    ? "linear-gradient(135deg, #0f172a 30%, #E85D1A 100%)"
                    : "linear-gradient(135deg, #f1f5f9 30%, #F26522 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  letterSpacing: "-0.5px",
                  mt: 1,
                }}
              >
                Pick a category to begin
              </Typography>

              <Typography
                sx={{
                  fontSize: "14px",
                  color: mode === "light" ? "#94a3b8" : "#64748b",
                  textAlign: "center",
                  maxWidth: 340,
                  lineHeight: 1.75,
                }}
              >
                Select a category from the sidebar to explore the available tools.
              </Typography>

              {/* divider */}
              <Box sx={{ width: 56, height: 2.5, borderRadius: 2, background: "linear-gradient(90deg, #F26522, #a855f7)", opacity: 0.55 }} />

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 1.4, width: "100%" }}>
                {visibleCategories.map((cat) => (
                  <Box
                    key={cat.id}
                    onClick={() => onSelectCategory && onSelectCategory(cat)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.8,
                      px: 2,
                      py: 1.2,
                      borderRadius: "14px",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: "pointer",
                      border: `1px solid ${mode === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.10)"}`,
                      bgcolor: mode === "light" ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.05)",
                      backdropFilter: "blur(10px)",
                      color: mode === "light" ? "#475569" : "#94a3b8",
                      transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                      "&:hover": {
                        borderColor: "rgba(232,93,26,0.50)",
                        color: "#E85D1A",
                        bgcolor: mode === "light" ? "rgba(255,247,244,0.95)" : "rgba(232,93,26,0.12)",
                        boxShadow: "0 6px 20px rgba(232,93,26,0.16)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box sx={{ fontSize: "16px", lineHeight: 1 }}>{cat.icon}</Box>
                    {cat.category}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Categories / Tool Cards ── */}
        {activeCategory && (
          <Box sx={{ maxWidth: 900, width: "100%", mx: "auto", px: { xs: 2, md: 3 }, py: 4 }}>
            {displayCategories.map((category) => (
              <Box key={category.id} sx={{ mb: 5 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2.5, gap: 1.5 }}>
                  <Box sx={{ width: 3, height: 20, borderRadius: 2, background: "linear-gradient(180deg, #F26522 0%, #f59e0b 100%)" }} />
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.9px",
                      color: mode === "light" ? "#64748b" : "#64748b",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.8,
                    }}
                  >
                    {category.icon} {category.category}
                  </Typography>
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: 2.5, alignItems: "stretch" }}>
                  {category.tools.map((tool) => (
                    <Box
                      key={tool.id}
                      onClick={() => handleCardClick(tool)}
                      sx={{
                        display: "flex",
                        alignItems: "stretch",
                        minWidth: 0,
                        opacity: tool.upcoming ? 0.55 : 1,
                        transition: "transform 0.25s cubic-bezier(0.4,0,0.2,1), box-shadow 0.25s",
                        cursor: tool.upcoming ? "default" : "pointer",
                        borderRadius: "16px",
                        ...(!tool.upcoming && {
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: "0 20px 48px rgba(232,93,26,0.18), 0 4px 16px rgba(0,0,0,0.10)",
                          },
                        }),
                      }}
                    >
                      <LiquidGlassCard
                        blurIntensity="md"
                        shadowIntensity="md"
                        glowIntensity="md"
                        borderRadius="16px"
                        background={mode === "light" ? "rgba(255,255,255,0.65)" : "rgba(18,24,38,0.60)"}
                        style={{ width: "100%", minHeight: 230 }}
                      >
                        {/* top accent line */}
                        <Box sx={{ height: 3, background: tool.upcoming ? "rgba(148,163,184,0.3)" : "linear-gradient(90deg, #F26522 0%, #f59e0b 100%)", borderRadius: "16px 16px 0 0" }} />

                        <Box
                          sx={{
                            p: "24px 22px 22px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            height: "100%",
                            position: "relative",
                          }}
                        >
                          {/* icon badge */}
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "14px",
                              background: tool.upcoming
                                ? mode === "light" ? "rgba(148,163,184,0.12)" : "rgba(71,85,105,0.15)"
                                : "linear-gradient(135deg, rgba(232,93,26,0.15) 0%, rgba(245,158,11,0.10) 100%)",
                              border: `1px solid ${tool.upcoming ? "rgba(148,163,184,0.15)" : "rgba(232,93,26,0.20)"}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mb: 1.5,
                              flexShrink: 0,
                              fontSize: "22px",
                              color: tool.upcoming
                                ? mode === "light" ? "#94a3b8" : "#475569"
                                : "#E85D1A",
                              "& svg": { fontSize: "22px" },
                            }}
                          >
                            {tool.icon}
                          </Box>

                          <Box sx={{ flexShrink: 0, wordBreak: "break-word" }}>
                            <Typography
                              sx={{
                                fontSize: "16px",
                                fontWeight: 700,
                                color: mode === "light" ? "#1e293b" : "#f1f5f9",
                                lineHeight: 1.25,
                                mb: 0.3,
                              }}
                            >
                              {tool.label}
                            </Typography>
                            {tool.tag && (
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  color: "#E85D1A",
                                  fontWeight: 600,
                                  lineHeight: 1.2,
                                }}
                              >
                                {tool.tag}
                              </Typography>
                            )}
                          </Box>

                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: mode === "light" ? "#94a3b8" : "#64748b",
                              lineHeight: 1.65,
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              mt: 0.5,
                            }}
                          >
                            {tool.description}
                          </Typography>

                          {(tool.upcoming || tool.inProgress || tool.status) && (
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: 14,
                                right: 14,
                                px: 1,
                                py: 0.25,
                                borderRadius: "6px",
                                bgcolor: tool.inProgress
                                  ? "rgba(59,130,246,0.10)"
                                  : "rgba(148,163,184,0.10)",
                                border: `1px solid ${tool.inProgress ? "rgba(59,130,246,0.25)" : "rgba(148,163,184,0.18)"}`,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "9px",
                                  fontWeight: 700,
                                  letterSpacing: "0.5px",
                                  textTransform: "uppercase",
                                  color: tool.inProgress
                                    ? "#3b82f6"
                                    : mode === "light" ? "#94a3b8" : "#475569",
                                }}
                              >
                                {tool.status || (tool.inProgress ? "In Progress" : "Unavailable")}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </LiquidGlassCard>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
